import React from 'react'
import { Translate } from 'react-redux-i18n'
import styled from 'styled-components'
import ReactTooltip from 'react-tooltip'
import Autosuggest from 'react-autosuggest'
import InputMoment from 'input-moment'
import Select from 'react-select'
import { connect } from 'react-redux'
import moment from 'moment'
import ReactModel from 'react-modal'

import { touchCheckInServiceTxnField } from '../../actions/checkin'
import ProductSearchSelect from '../../modules/product/ProductSearchSelect'
import { debounce } from 'lodash'

// HOC

const withTouch = (Component) =>
  connect(null, { touchCheckInServiceTxnField })(withTouchComponent(Component))

const withTouchComponent =
  (Component) =>
  ({ touchCheckInServiceTxnField, onBlur, ...props }) => {
    const handleBlur = (e) => {
      touchCheckInServiceTxnField(props.name)
      if (onBlur) onBlur(e)
    }
    return <Component {...props} onBlur={handleBlur} />
  }

const createFieldGroup = (Component) => (props) => {
  const handleChange = (value) => {
    withField(props.name, props.onChange)(value)
  }

  return <FieldGroup {...props} component={Component} onChange={handleChange} />
}

const withField = (name, onChange) => (value) => onChange(name, value)

const FieldGroup = ({
  name,
  value,
  onChange,
  onBlur,
  i18n,
  error,
  touched = false,
  pristine = true,
  component: Component,
  ...rest
}) => {
  const hasError = (touched || !pristine) && error

  return (
    <div className="w-full">
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Translate value={i18n} />
          {hasError && (
            <span className="ml-2 text-red-600">
              <svg
                className="inline w-4 h-4"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          )}
        </label>
        <FieldBlock onBlur={onBlur} hasError={hasError}>
          <Component name={name} value={value} onChange={onChange} {...rest} />
        </FieldBlock>
      </div>
      <ReactTooltip effect="solid" />
    </div>
  )
}

const FieldBlock = ({ children, onBlur }) => (
  <div
    className="flex flex-col w-full max-w-full overflow-hidden"
    onBlur={onBlur}
  >
    {children}
  </div>
)

// MARK: - Fields

// TextField

const TextField = ({ value, onChange, type = 'text', disabled }) => (
  <input
    className="w-full h-12 px-4 text-base text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
    type={type}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    spellCheck={false}
  />
)
export const TextFieldGroup = withTouch(createFieldGroup(TextField))

const TextAreaField = ({ value, onChange }) => (
  <textarea
    className="w-full px-4 py-3 text-base text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-y min-h-[96px]"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    spellCheck={false}
    rows={3}
  />
)

export const TextAreaFieldGroup = withTouch(createFieldGroup(TextAreaField))

// AutosuggestTextField

// Styled wrapper for Autosuggest - keeping styled-components for now since global CSS is needed
const AutosuggestWrapper = styled.div`
  .react-autosuggest__input {
    width: 100%;
    height: 48px;
    padding: 0 16px;
    font-size: 16px;
    color: #111827;
    background-color: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    transition: all 0.2s;
  }

  .react-autosuggest__input:focus {
    outline: none;
    border-color: #aa6326;
    box-shadow: 0 0 0 3px rgba(170, 99, 38, 0.2);
  }

  .react-autosuggest__suggestions-container {
    position: relative;
  }

  .react-autosuggest__suggestions-container--open {
    position: absolute;
    width: 100%;
    margin-top: 4px;
    z-index: 1000;
  }

  .react-autosuggest__suggestions-list {
    margin: 0;
    padding: 0;
    list-style-type: none;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 300px;
    overflow-y: auto;
  }

  .react-autosuggest__suggestion {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;
  }

  .react-autosuggest__suggestion:last-child {
    border-bottom: none;
  }

  .react-autosuggest__suggestion--highlighted {
    background-color: #fdf7f0;
    color: #aa6326;
  }
`

class AutosugguestTextField extends React.Component {
  state = { suggestions: [] }

  constructor(props) {
    super(props)

    this.onSuggestionsFetchRequested = debounce(
      this.onSuggestionsFetchRequested,
      300,
    )
  }

  onSuggestionsFetchRequested = async ({ value }) => {
    const { loadSuggestions, getSuggestions } = this.props
    if (loadSuggestions) {
      this.setState({
        suggestions: await loadSuggestions(value),
      })
    } else {
      this.setState({
        suggestions: getSuggestions(value),
      })
    }
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    })
  }

  render() {
    const {
      value,
      onChange,
      getSuggestionValue,
      renderSuggestion,
      onSuggestionSelected,
    } = this.props
    const inputProps = {
      value: value || '',
      onChange: (event, { newValue }) => onChange(newValue),
      spellCheck: false,
    }
    const handleSuggestionSelected = (event, { suggestion }) => {
      if (onSuggestionSelected) onSuggestionSelected(suggestion)
    }
    return (
      <AutosuggestWrapper>
        <Autosuggest
          suggestions={this.state.suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          onSuggestionSelected={handleSuggestionSelected}
        />
      </AutosuggestWrapper>
    )
  }
}

export const AutosugguestTextFieldGroup = withTouch(
  createFieldGroup(AutosugguestTextField),
)

const toggleCalendar = (isOpen) => (state) => ({
  ...state,
  isCalendarOpen: isOpen,
})

const openCalendar = toggleCalendar(true)
const closeCalendar = toggleCalendar(false)

class DateTimeField extends React.Component {
  state = {
    isCalendarOpen: false,
  }

  renderDateTime() {
    const { dateFormat } = this.props
    const value = this.getValue()
    if (value) return value.format(dateFormat)
    return null
  }

  openCalendar = () => this.setState(openCalendar)
  closeCalender = () => this.setState(closeCalendar)

  handleValueChange = (prevMoment) => {
    this.props.onChange(new moment(prevMoment))
  }

  calendarParentSelector = () => document.getElementById('root')

  getValue = () => this.props.value || moment()

  render() {
    const { disabled } = this.props
    return (
      <DateTimeFieldLabelLayout>
        <DateTimeFieldLabel
          onClick={disabled ? undefined : this.openCalendar}
          disabled={disabled}
        >
          {this.renderDateTime()}
        </DateTimeFieldLabel>
        <ReactModel
          isOpen={this.state.isCalendarOpen}
          onRequestClose={this.closeCalender}
          style={dateTimeFieldCalendarModalStyle}
          parentSelector={this.calendarParentSelector}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
        >
          <CalendarModalBodyLayout>
            <InputMoment
              moment={this.getValue()}
              onChange={this.handleValueChange}
            />
          </CalendarModalBodyLayout>
        </ReactModel>
      </DateTimeFieldLabelLayout>
    )
  }
}

ReactModel.setAppElement(document.getElementById('root'))

const dateTimeFieldCalendarModalStyle = {
  overlay: {
    backgroundColor: '#757575ba', // black overlay partial see through
    zIndex: '2000',
  },
  content: {
    pointerEvents: 'none',
    backgroundColor: undefined,
    display: 'flex',
    border: '0',
  },
}

const CalendarModalBodyLayout = ({ children }) => (
  <div className="pointer-events-auto m-auto bg-gray-200 rounded">
    {children}
  </div>
)

const DateTimeFieldLabelLayout = ({ children }) => (
  <div className="flex flex-1">{children}</div>
)

const DateTimeFieldLabel = ({ children, onClick, disabled }) => (
  <div
    className={`
      text-base h-12 px-4 w-full bg-white border border-gray-300 rounded-lg
      text-gray-900 transition-all flex items-center
      ${
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'cursor-pointer hover:border-primary-600 hover:shadow-sm focus:outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-500'
      }
    `}
    onClick={onClick}
  >
    {children}
  </div>
)

export const DateTimeFieldGroup = withTouch(createFieldGroup(DateTimeField))

// NativeDateTimeField

const NativeDateTimeField = ({ value, onChange, disabled }) => {
  // Convert moment to datetime-local format (YYYY-MM-DDTHH:mm)
  const momentToDatetimeLocal = (momentValue) => {
    if (!momentValue) return ''
    return moment(momentValue).format('YYYY-MM-DDTHH:mm')
  }

  // Convert datetime-local string to moment
  const datetimeLocalToMoment = (datetimeString) => {
    if (!datetimeString) return null
    return moment(datetimeString)
  }

  const handleChange = (e) => {
    const datetimeValue = e.target.value
    const momentValue = datetimeLocalToMoment(datetimeValue)
    onChange(momentValue)
  }

  return (
    <input
      type="datetime-local"
      className="h-12 px-4 text-base text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
      value={momentToDatetimeLocal(value)}
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

export const NativeDateTimeFieldGroup = withTouch(
  createFieldGroup(NativeDateTimeField),
)

// NativeSelectField

const NativeSelectField = ({ value, onChange, options, disabled }) => (
  <select
    className="w-full h-12 px-4 text-base text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
  >
    <option value="">Select...</option>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
)

export const NativeSelectFieldGroup = withTouch(
  createFieldGroup(NativeSelectField),
)

// SelectField

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '48px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#aa6326' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(170, 99, 38, 0.2)' : 'none',
    '&:hover': {
      borderColor: '#aa6326',
    },
    fontSize: '16px',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#aa6326'
      : state.isFocused
        ? '#fdf7f0'
        : 'white',
    color: state.isSelected ? 'white' : '#111827',
    '&:active': {
      backgroundColor: '#aa6326',
    },
  }),
}

const SelectField = ({
  value,
  onChange,
  options,
  simpleValue = true,
  ...props
}) => (
  <Select
    value={value}
    options={options}
    simpleValue={simpleValue}
    onChange={(optionOrValue) =>
      onChange(selectValue(optionOrValue, simpleValue))
    }
    openOnFocus
    styles={customSelectStyles}
    {...props}
  />
)

export const SelectFieldGroup = withTouch(createFieldGroup(SelectField))

const SelectAsyncField = ({
  value,
  onChange,
  loadOptions,
  simpleValue = true,
  ...props
}) => (
  <Select.Async
    value={value}
    loadOptions={loadOptions}
    simpleValue={simpleValue}
    onChange={(optionOrValue) =>
      onChange(selectValue(optionOrValue, simpleValue))
    }
    openOnFocus
    styles={customSelectStyles}
    {...props}
  />
)

export const SelectAsyncFieldGroup = withTouch(
  createFieldGroup(SelectAsyncField),
)

const selectValue = (optionOrValue, isSimpleValue) => {
  if (isSimpleValue) return optionOrValue // it's is value already
  return optionOrValue ? optionOrValue.value : null
}

export const ProductSearchSelectFieldGroup = withTouch(
  createFieldGroup(ProductSearchSelect),
)
